// ui.js — generic UI primitives used across the dashboard.
//
//   showToast(kind, msg, opts)         transient bottom-right toast
//   showConfirm(title, body, label, opts)
//                                      modal confirm/cancel
//                                      returns Promise<boolean>
//   showPrompt(title, body, opts)      modal text input
//                                      returns Promise<string | null>
//   closeAllConfirms()                 force-close any open confirm
//   isCloudMode()                      deploy-mode detection
//
// All four modal helpers operate on DOM elements that exist in the
// HTML shell (#toast-container, #confirm-backdrop, #prompt-backdrop).
// In daemon mode those elements still exist — they're CSS-hidden
// only for cloud-only flows.

import { $ } from "./dom.js";
import { escapeHTML as _escapeHTML } from "./util.js"; // future-proof; not used today
void _escapeHTML;

// showToast displays a bottom-right transient notification.
//   kind     — "success" | "error" | "info" (drives label + color)
//   msg      — plain text body
//   opts.duration  — ms before auto-dismiss; defaults: success/info
//                    3500ms, error 6000ms. Set to 0 to disable.
export function showToast(kind, msg, opts) {
  const container = document.getElementById("toast-container");
  if (!container) return;
  const duration =
    (opts && opts.duration) || (kind === "error" ? 6000 : 3500);
  // Word labels in place of icons. No emoji, no glyphs.
  const labels = { success: "Done", error: "Error", info: "Heads up" };
  const toast = document.createElement("div");
  toast.className = "toast toast-" + kind;
  const kindEl = document.createElement("span");
  kindEl.className = "toast-kind";
  kindEl.textContent = labels[kind] || "Heads up";
  const body = document.createElement("span");
  body.className = "toast-body";
  body.textContent = msg;
  const close = document.createElement("button");
  close.className = "toast-close";
  close.setAttribute("aria-label", "Dismiss");
  close.textContent = "×";
  const dismiss = () => {
    toast.classList.add("toast-out");
    setTimeout(() => toast.remove(), 200);
  };
  close.addEventListener("click", dismiss);
  toast.appendChild(kindEl);
  toast.appendChild(body);
  toast.appendChild(close);
  container.appendChild(toast);
  if (duration > 0) setTimeout(dismiss, duration);
}

// showConfirm displays a modal with Cancel + Confirm buttons.
// Resolves to true if the user clicked the confirm button, false
// otherwise (cancel, backdrop click, hideCancel-with-backdrop).
//
// opts.hideCancel  — only show the confirm button; backdrop click
//                    resolves true. Useful for "Got it" prompts.
export function showConfirm(title, bodyHTML, actionLabel, opts) {
  return new Promise((resolve) => {
    const hideCancel = !!(opts && opts.hideCancel);
    $("confirm-title").textContent = title;
    $("confirm-body").innerHTML = bodyHTML;
    $("confirm-ok").textContent = actionLabel || "Confirm";
    $("confirm-cancel").style.display = hideCancel ? "none" : "";
    $("confirm-backdrop").classList.add("open");
    const cleanup = () => {
      $("confirm-backdrop").classList.remove("open");
      $("confirm-cancel").style.display = "";
      $("confirm-ok").removeEventListener("click", onOK);
      $("confirm-cancel").removeEventListener("click", onCancel);
      $("confirm-backdrop").removeEventListener("click", onBackdrop);
    };
    const onOK = () => {
      cleanup();
      resolve(true);
    };
    const onCancel = () => {
      cleanup();
      resolve(false);
    };
    const onBackdrop = (e) => {
      if (e.target === $("confirm-backdrop")) {
        cleanup();
        resolve(hideCancel ? true : false);
      }
    };
    $("confirm-ok").addEventListener("click", onOK);
    $("confirm-cancel").addEventListener("click", onCancel);
    $("confirm-backdrop").addEventListener("click", onBackdrop);
  });
}

// closeAllConfirms force-closes any open confirm modal. Used when a
// button inside the modal body needs to dismiss it before navigating
// away.
export function closeAllConfirms() {
  const bd = $("confirm-backdrop");
  if (bd) bd.classList.remove("open");
}

// showPrompt displays a modal with a single text input + OK/Cancel.
// Resolves to the trimmed input value or null (cancel / Escape /
// backdrop click).
//
// opts.initialValue / placeholder / actionLabel control the input
// state + the OK button label.
export function showPrompt(title, bodyText, opts) {
  return new Promise((resolve) => {
    const o = opts || {};
    document.getElementById("prompt-title").textContent = title;
    document.getElementById("prompt-body").textContent = bodyText || "";
    const input = document.getElementById("prompt-input");
    input.value = o.initialValue || "";
    input.placeholder = o.placeholder || "";
    document.getElementById("prompt-ok").textContent =
      o.actionLabel || "OK";
    const backdrop = document.getElementById("prompt-backdrop");
    backdrop.classList.add("open");
    setTimeout(() => input.focus(), 50);
    const cleanup = () => {
      backdrop.classList.remove("open");
      document
        .getElementById("prompt-ok")
        .removeEventListener("click", onOK);
      document
        .getElementById("prompt-cancel")
        .removeEventListener("click", onCancel);
      backdrop.removeEventListener("click", onBackdrop);
      input.removeEventListener("keydown", onKey);
    };
    const onOK = () => {
      const v = input.value.trim();
      cleanup();
      resolve(v || null);
    };
    const onCancel = () => {
      cleanup();
      resolve(null);
    };
    const onBackdrop = (e) => {
      if (e.target === backdrop) {
        cleanup();
        resolve(null);
      }
    };
    const onKey = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onOK();
      }
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    };
    document.getElementById("prompt-ok").addEventListener("click", onOK);
    document
      .getElementById("prompt-cancel")
      .addEventListener("click", onCancel);
    backdrop.addEventListener("click", onBackdrop);
    input.addEventListener("keydown", onKey);
  });
}

// isCloudMode returns true when the dashboard is running in cloud-
// pages or coordinator-hosted mode (anything other than the local
// daemon). Reads window.AC_CONFIG.DEPLOY_MODE, populated by
// config.js in <head>.
export function isCloudMode() {
  const cfg = window.AC_CONFIG;
  return !!(
    cfg && cfg.DEPLOY_MODE && cfg.DEPLOY_MODE !== "local-daemon"
  );
}

// isLocalDaemonMode is the mirror of isCloudMode: the dashboard is
// being served by the user's own daemon at 127.0.0.1.
export function isLocalDaemonMode() {
  const cfg = window.AC_CONFIG;
  return !!(cfg && cfg.DEPLOY_MODE === "local-daemon");
}

// usesCloudShapedEndpoints returns true when the dashboard should
// hit `/v1/teams/*` endpoints (the "cloud" data shape) for its
// Overview / counts / leases fetches.
//
// - In cloud-pages mode (`DEPLOY_MODE != local-daemon`): the worker
//   serves these directly.
// - In local-daemon mode (v0.31.2+): the daemon serves the same
//   `/v1/teams/*` paths via `teamSoloOrLocal` (which proxies to local
//   handlers in solo mode, and to the coordinator when team-connected).
//
// Pre-v0.31.9 the Overview gated its cloud fetch loop on
// `isCloudMode()` alone, so local-daemon users got blank hero tiles
// — the fetches that hydrate state.leasesAll / state.analytics never
// fired. Use this predicate for ANY data-fetch decision that should
// fire against the cloud-shape endpoints; reserve `isCloudMode()` for
// decisions that are specifically about being on dashboard.agentcollision.com
// (e.g. SSE via stream-token, billing UI, supabase auth).
export function usesCloudShapedEndpoints() {
  return isCloudMode() || isLocalDaemonMode();
}
